// Utility functions for GitHub Cost Center components
import { Theme } from '@material-ui/core';
import { CostCenterRecord } from '../interfaces/types';

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const formatPeriod = (value: string) => {
  const [year, month] = value.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
  }).format(date);
};

export const mapSourceToCorrectValue = (
  source: string,
  isFromEmu: boolean,
): string => {
  if (isFromEmu) {
    return 'CBRE-EMU';
  }
  return 'CBRE-ENT';
};

export const getSourceClassName = (source: string) =>
  source === 'CBRE-EMU' ? 'cost-center-source--emu' : 'cost-center-source--ent';

export const getCurrentMonthValue = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');

  return `${today.getFullYear()}-${month}`;
};

export const formatCurrency = (amount: number): string => {
  // For values less than 1, show at least 2 decimal places
  if (Math.abs(amount) < 1) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatNumber = (num: number): string => {
  if (num < 10000) {
    return num.toLocaleString();
  }

  const formatWithSuffix = (value: number, suffix: string): string => {
    const formatted = value.toFixed(2);
    // Remove unnecessary trailing zeros and decimal point
    return formatted.replace(/\.?0+$/, '') + suffix;
  };

  if (num >= 1000000000) {
    // Billions
    return formatWithSuffix(num / 1000000000, 'B');
  } else if (num >= 1000000) {
    // Millions
    return formatWithSuffix(num / 1000000, 'M');
  }
  // else if (num >= 100000) {
  //   // Lakhs
  //   return formatWithSuffix(num / 100000, 'L');
  // }
  else {
    // Thousands
    return formatWithSuffix(num / 1000, 'K');
  }
};

export const formatCurrencyAbbreviated = (amount: number): string => {
  if (amount < 10000) {
    return formatCurrency(amount);
  }

  const formatWithSuffix = (value: number, suffix: string): string => {
    const formatted = value.toFixed(2);
    // Remove unnecessary trailing zeros and decimal point
    return '$' + formatted.replace(/\.?0+$/, '') + suffix;
  };

  if (amount >= 1000000000) {
    // Billions
    return formatWithSuffix(amount / 1000000000, 'B');
  } else if (amount >= 1000000) {
    // Millions
    return formatWithSuffix(amount / 1000000, 'M');
  }
  // else if (amount >= 100000) {
  //   // Lakhs
  //   return formatWithSuffix(amount / 100000, 'L');
  // }
  else {
    // Thousands
    return formatWithSuffix(amount / 1000, 'K');
  }
};

export const generateCSV = (
  headers: string[],
  rows: string[][],
  filename: string,
) => {
  const csvContent = [
    headers.map(h => `"${h}"`).join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const calculateUtilization = (
  consumedAmount: number,
  budgetAmount: number,
): number => {
  if (budgetAmount === 0) return 0;
  return Math.min((consumedAmount / budgetAmount) * 100, 100);
};

export const filterCostCenterData = (
  costCenters: CostCenterRecord[],
  costCenterName?: string,
  source?: string,
  monthAndYearWise = false,
  skipUniqueCheck = false,
):
  | CostCenterRecord[]
  | { monthAndYear: any; filteredCostCenters: CostCenterRecord[] } => {
  const seenIds = new Set<string>();
  const monthAndYearWiseCostCenters: any = {};
  const filteredCostCenters =
    costCenters?.filter((costCenter: CostCenterRecord) => {
      if (
        costCenter.state === 'active' &&
        !seenIds.has(costCenter.costCenterId)
      ) {
        let nameFound = true;
        let sourceFound = true;
        if (!skipUniqueCheck) {
          seenIds.add(costCenter.costCenterId);
        }
        if (costCenterName) {
          nameFound = costCenter.name
            .toLowerCase()
            .includes(costCenterName.toLowerCase());
        }
        if (source) {
          sourceFound =
            costCenter.source.toLowerCase() === source.toLowerCase();
        }
        return nameFound && sourceFound;
      }
      return false;
    }) || [];
  seenIds.clear();
  if (monthAndYearWise) {
    filteredCostCenters.forEach((costCenter: CostCenterRecord) => {
      if (!monthAndYearWiseCostCenters[costCenter.year]) {
        monthAndYearWiseCostCenters[costCenter.year] = {};
      }
      if (!monthAndYearWiseCostCenters[costCenter.year][costCenter.month]) {
        monthAndYearWiseCostCenters[costCenter.year][costCenter.month] = [];
      }
      monthAndYearWiseCostCenters[costCenter.year][costCenter.month].push(
        costCenter,
      );
    });
    return { monthAndYear: monthAndYearWiseCostCenters, filteredCostCenters };
  }
  return filteredCostCenters;
};

export const getThemeCSSVariables = (theme: Theme) => {
  return {
    '--cbre-primary-text': theme.palette.type === 'dark' ? '#fff' : '#003f2d',
    '--cbre-heading-text': theme.palette.type === 'dark' ? '#fff' : '#1a1a1a',
    '--cbre-sub-heading-text':
      theme.palette.type === 'dark' ? '#fff' : '#555555',
    '--cbre-secondary-text': theme.palette.type === 'dark' ? '#fff' : '#6e6e6e',
    '--cbre-button-text': '#ffffff',
    '--cbre-surface': theme.palette.type === 'dark' ? '#424242' : '#ffffff',
    '--cbre-cost-center-table-thead-bg':
      theme.palette.type === 'dark' ? '#424242' : '#f7fafc',
    '--cbre-cost-center-table-tbody-nth-child-bg':
      theme.palette.type === 'dark' ? '#424242' : '#f8f9f8',
    '--cbre-cost-center-overview-row-hover':
      theme.palette.type === 'dark' ? '#555555' : '#f1f4f2',

    '--cbre-page-background':
      theme.palette.type === 'dark' ? '#333333' : '#f2f2f2',
    '--cbre-card-border': theme.palette.type === 'dark' ? '#555555' : '#e2e8f0',
    '--cbre-card-additional-details-text':
      theme.palette.type === 'dark' ? '#fff' : '#767676',
    '--cbre-card-additional-details-value':
      theme.palette.type === 'dark' ? '#fff' : '#1a1a1a',
    '--cbre-kpi-section-border':
      theme.palette.type === 'dark' ? '#555555' : '#dde3df',
    '--cbre-search-field-bg':
      theme.palette.type === 'dark' ? '#424242' : '#fff',
    '--cbre-search-input-color':
      theme.palette.type === 'dark' ? '#fff' : '#1a1a1a',
    '--cbre-search-input-icon-color':
      theme.palette.type === 'dark' ? '#fff' : 'rgba(26, 26, 26, 0.6)',
    '--cbre-cost-center-pill--status-active':
      theme.palette.type === 'dark' ? '#17e68d' : '#2e7d32',
    '--eds-button-primary-bg':
      theme.palette.type === 'dark' ? '#17e68d' : '#003f2d',
    '--eds-button-primary-color':
      theme.palette.type === 'dark' ? '#17e68d' : '#003f2d',
    '--eds-search-field-border-focus':
      theme.palette.type === 'dark'
        ? '#FFF'
        : 'var(--eds-search-field-border-focus)',
    '--cbre-MuiButton-containedPrimary':
      theme.palette.type === 'dark' ? '#17e68d' : '#003f2d',
    '--cbre-pagination-icon-btn-hover':
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.8)'
        : 'rgba(0, 63, 45, 0.08)',
    '--cbre-pagination-icon-btn-disabled':
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.48)'
        : 'rgba(26, 26, 26, 0.48)',
    '--cbre-pagination-rows-button':
      theme.palette.type === 'dark'
        ? 'rgba(85, 85, 85, 0.12)'
        : 'rgba(0, 63, 45, 0.12)',
    '--cbre-pagination-rows-button-hover':
      theme.palette.type === 'dark'
        ? 'rgba(85, 85, 85, 0.2)'
        : 'rgba(0, 63, 45, 0.2)',
    '--cbre-expand-button-color':
      theme.palette.type === 'dark' ? '#fff' : '#666',
    '--cbre-inner-table-bg':
      theme.palette.type === 'dark' ? '#424242' : '#f8f9fa',
    '--eds-icon-button-icon-primary-color':
      theme.palette.type === 'dark' ? '#FFF' : '#003f2d',
  };
};
